"use client";

import SignIn from "@components/sign-in";
import Link from "next/link";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui/components/ui/popover";
import {
  ProfileAvatar,
  ProfileAvatarProps,
} from "@repo/ui/components/ui/avatar";
import Header from "@repo/ui/components/ui/header/index";
import { useTranslations } from "next-intl";
import { Session } from "next-auth";
import { usePathname } from "next/navigation";
import {
  NotificationsIcon,
  useNotificationsSubscription,
  useNotifications,
  useNotificationsCounter,
  useDeleteNotification,
  useMarkNotificationAsRead,
  Notifications,
} from "@repo/next-shared/components/notifications";
import { cn } from "@repo/ui/lib/utils";
import NotificationsList from "@repo/next-shared/components/notifications/notifications-list";
import OfferNotification from "@/(private)/components/offer-notification";

type NavProps = {
  session: Session | null;
  children?: React.ReactNode;
};

export default function Nav({ session, children }: NavProps) {
  const t = useTranslations("Nav");
  const pathname = usePathname();
  const { data: notifications } = useNotifications("seller");
  const { data: notificationsCounter } = useNotificationsCounter("seller");
  const onDeleteNotification = useDeleteNotification();
  const onMarkAsReadNotification = useMarkNotificationAsRead();
  useNotificationsSubscription(session, "seller");

  const primaryItems = [
    { text: t("about"), href: "/#about", public: true },
    { text: t("how_works"), href: "/#how-works", public: true },
    { text: t("tutorial"), href: "/#tutorial", public: true },
    {
      text: t("dashboard"),
      href: "/dashboard",
      public: false,
      selected: pathname === "/dashboard",
    },
    {
      text: t("orders"),
      href: "/orders/open",
      public: false,
      selected:
        pathname === "/orders/open" ||
        pathname === "/orders/delivered" ||
        pathname === "/orders/returned",
      items: [
        {
          text: t("pending_orders"),
          href: "/orders/open",
          selected: pathname === "/orders/open",
        },
        {
          text: t("shipped_orders"),
          href: "/orders/delivered",
          selected: pathname === "/orders/delivered",
        },
        {
          text: t("returned_orders"),
          href: "/orders/returned",
          selected: pathname === "/orders/returned",
        },
      ],
    },
    {
      text: t("catalog"),
      href: "/products/catalog",
      public: false,
      selected: pathname === "/products/catalog",
      items: [
        {
          text: t("product_catalog"),
          href: "/products/catalog",
          selected: pathname === "/products/catalog",
        },
        {
          text: t("product_upload_management"),
          href: "/products/upload/manual",
          selected: pathname === "/products/upload/manual",
        },
      ],
    },
    {
      text: t("offers"),
      href: "/offers/open",
      public: false,
      isPrimary: true,
      selected: pathname === "/offers/open",
      items: [
        {
          text: t("open_offers"),
          href: "/offers/open",
          selected: pathname === "/offers/open",
        },
        {
          text: t("archived_offers"),
          href: "/offers/archived",
          selected: pathname === "/offers/archived",
        },
      ],
    },
    // {
    //   text: t("help"),
    //   isPrimary: true,
    //   href: "/help",
    //   public: true,
    //   common: true,
    // },
  ].filter((item) => (session && !item.public) || (!session && item.public));

  return (
    <Header
      primaryItems={primaryItems}
      LinkCmp={Link}
      logoHref={session ? "/dashboard" : "/"}
    >
      {{
        closed: session ? (
          <div className="flex gap-[20px] max-md:gap-[10px] items-center">
            <Popover>
              <PopoverTrigger>
                <ProfileAvatar
                  {...(session.user as ProfileAvatarProps)}
                  notificationsCounter={notificationsCounter}
                />
              </PopoverTrigger>
              <PopoverContent align="end">
                <div className="divide-y">
                  <PopoverClose asChild>
                    <Link href="/profile/company-data">
                      <div className="p-[20px]">{t("go_to_profile")}</div>
                    </Link>
                  </PopoverClose>
                  <PopoverClose asChild className="hidden max-md:block">
                    <Link href="/notifications">
                      <div
                        className={cn("p-[20px] flex items-center gap-2", {
                          "font-bold text-primary-black":
                            !!notificationsCounter,
                        })}
                      >
                        {t("notifications")}
                        {!!notificationsCounter && (
                          <div className="w-2 h-2 bg-primary-magenta rounded-full"></div>
                        )}
                      </div>
                    </Link>
                  </PopoverClose>
                  <div className="p-[20px]">{children}</div>
                </div>
              </PopoverContent>
            </Popover>
            <div className="max-md:hidden h-[46px]">
              <NotificationsIcon counter={notificationsCounter}>
                <Notifications notificationsUrl="/notifications">
                  <NotificationsList
                    deleteNotification={(id) =>
                      onDeleteNotification.mutate({ id, portal: "seller" })
                    }
                    markAsReadNotification={(id) =>
                      onMarkAsReadNotification.mutate({ id, portal: "seller" })
                    }
                    notifications={notifications?.list || []}
                    inline={true}
                    customContent={{
                      key: "offerId",
                      element: (offerId) => (
                        <OfferNotification offerId={offerId} />
                      ),
                    }}
                  ></NotificationsList>
                </Notifications>
              </NotificationsIcon>
            </div>
          </div>
        ) : (
          <div className="flex gap-[40px] max-md:gap-[12px] items-center">
            <SignIn variant="outline" />
          </div>
        ),
        open: <></>,
      }}
    </Header>
  );
}
